import {
  DATA_CALCULATION_SETUP_VALUES,
  VALIDATION_FIELD_VALUES,
  MAX_TICKER_LENGTH,
  TYPE_OF_MODIFICATION,
  AT_MARKET_OPEN,
  PRICE_RETURN,
} from './constants';
import { ORGANIZATION_PREFIXES } from '../../../constants/organizationPrefixConstants';
import { generateBulkBasketErrorToast } from './index';
// Constants for divisor determination setup values
const DIVISOR_DETERMINATION_VALUES = {
  IMMEDIATELY: 'IMMEDIATELY',
  PREVIOUS_EOD: 'PREVIOUS_EOD',
};

/**
 * Generates a validation error message for required fields with specific identifiers.
 * @param {string} fieldName - The name of the field (e.g., 'Shares', 'Coverage').
 * @param {string} authOrgPrefix - The organization prefix ('UB' for SEDOL, others for ISIN).
 * @param {Array<string>} identifiers - Array of ISINs or SEDOLs that are missing the field.
 * @returns {string} Formatted error message.
 */
const getRequiredFieldError = (fieldName, authOrgPrefix, identifiers) => {
  const identifierType = authOrgPrefix === ORGANIZATION_PREFIXES.UBS ? 'SEDOL(s)' : 'ISIN(s)';
  return `${fieldName} are required for the following ${identifierType}: ${identifiers.join(', ')}`;
};

/**
 * Validates if a field value is in the accepted list of values
 * @param {string} key - The field key to validate
 * @param {string} field - The field value to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateFieldValue = (key, field) => {
  if (!key || !field) return false;
  const acceptedValues = VALIDATION_FIELD_VALUES[key];
  return !!acceptedValues?.includes(field);
};

/**
 * Validates required string fields
 * @param {string} value - The value to validate
 * @returns {boolean} True if valid (non-empty after trim), false otherwise
 */
const validateRequiredString = value => {
  return value && typeof value === 'string' && value.trim() !== '';
};

/**
 * Validates required number fields
 * @param {number} value - The value to validate
 * @returns {boolean} True if valid (greater than 0), false otherwise
 */
const validateRequiredNumber = value => {
  return value !== null && value !== undefined && Number(value) > 0;
};

/**
 * Validates interest rate on cash
 * @param {any} interestRate - The interest rate value
 * @returns {boolean} True if valid, false otherwise
 */
const validateInterestRate = interestRate => {
  if (interestRate === null || interestRate === undefined || interestRate === '') {
    return false;
  }
  // Handle non-string/non-number types
  if (typeof interestRate !== 'string' && typeof interestRate !== 'number') {
    return false;
  }
  const numericValue = parseFloat(interestRate.toString().replace(/,/g, ''));
  return !Number.isNaN(numericValue) && numericValue >= 0;
};

/**
 * Calculates ticker length including org prefix
 * @param {string} symbol - The basket symbol
 * @param {string} orgPrefix - The organization prefix
 * @param {string} authOrgPrefix - The authenticated user's org prefix
 * @returns {number} Total ticker length
 */
const calculateTickerLength = (symbol, orgPrefix, authOrgPrefix) => {
  const prefixLength = orgPrefix?.length ?? authOrgPrefix?.length ?? 0;
  return (symbol?.length ?? 0) + prefixLength;
};

// ============================================================================
// FIELD-SPECIFIC VALIDATORS
// ============================================================================

/**
 * Validates basic required fields for a basket
 * @param {Object} basket - The basket object to validate
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateBasicRequiredFields = basket => {
  const errors = {};

  if (!validateRequiredString(basket?.name)) {
    errors.name = 'Name is required';
  }

  if (!validateRequiredString(basket?.currency)) {
    errors.currency = 'Currency is required';
  }

  if (!validateRequiredString(basket?.calculation_setup)) {
    errors.calculation_setup = 'Calculation frequency is required';
  }

  if (!validateRequiredString(basket?.calculation_type)) {
    errors.calculation_type = 'Return type is required';
  }

  if (!validateRequiredString(basket?.calculation_zone)) {
    errors.calculation_zone = 'Calculation zone is required';
  }

  return errors;
};

/**
 * Validates shares for a basket
 * @param {Object} basket - The basket object to validate
 * @param {Object} basketsWithoutShares - Object mapping symbols to arrays of identifiers without shares
 * @param {string} authOrgPrefix - The authenticated user's org prefix
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateShares = (basket, basketsWithoutShares, authOrgPrefix) => {
  const errors = {};
  const missingShares = basketsWithoutShares?.[basket?.symbol];

  if (missingShares && missingShares.length > 0) {
    errors.shares = getRequiredFieldError('Shares', authOrgPrefix, missingShares);
  }

  return errors;
};

/**
 * Validates coverage for a basket
 * @param {Object} basket - The basket object to validate
 * @param {Object} basketsWithoutCoverageInstruments - Object mapping symbols to arrays of identifiers without coverage
 * @param {string} authOrgPrefix - The authenticated user's org prefix
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateCoverage = (basket, basketsWithoutCoverageInstruments, authOrgPrefix) => {
  const errors = {};
  const missingCoverage = basketsWithoutCoverageInstruments?.[basket?.symbol];

  if (missingCoverage && missingCoverage.length > 0) {
    errors.coverage = getRequiredFieldError('Coverage', authOrgPrefix, missingCoverage);
  }

  return errors;
};

/**
 * Validates divisor-related fields for update mode
 * @param {Object} basket - The basket object to validate
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateDivisorForUpdate = basket => {
  const errors = {};

  if (basket?.automatically_adjust_divisor) {
    if (
      !basket?.divisor_determination_setup ||
      !validateFieldValue('divisor_determination_setup', basket.divisor_determination_setup)
    ) {
      errors.divisor_determination_setup = 'Divisor determination point is required';
    }
  } else if (!validateRequiredNumber(basket?.divisor)) {
    errors.divisor = 'Divisor is required';
  }

  return errors;
};

/**
 * Validates divisor-related fields for submit mode
 * @param {Object} basket - The basket object to validate
 * @param {Object} enableDivisorOrStartingValue - Object mapping symbols to divisor/starting value status
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateDivisorForSubmit = (basket, enableDivisorOrStartingValue) => {
  const errors = {};
  const symbol = basket?.symbol;
  const divisorOrStartingValue = enableDivisorOrStartingValue?.[symbol];

  if (divisorOrStartingValue?.statusStarting) {
    if (
      !basket?.divisor_determination_setup ||
      !validateFieldValue('divisor_determination_setup', basket.divisor_determination_setup)
    ) {
      errors.divisor_determination_setup = 'Divisor determination point is required';
    }
  }

  if (divisorOrStartingValue?.statusDivisor && !validateRequiredNumber(basket?.divisor)) {
    errors.divisor = 'Divisor is required';
  }

  if (divisorOrStartingValue?.statusStarting && !validateRequiredNumber(basket?.starting_value)) {
    errors.starting_value = 'Starting value is required';
  }

  return errors;
};

/**
 * Validates launch date and launch point for submit mode
 * @param {Object} basket - The basket object to validate
 * @param {boolean} shouldSendLaunchDate - Whether launch date should be validated
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateLaunchFields = (basket, shouldSendLaunchDate) => {
  const errors = {};

  if (shouldSendLaunchDate && !validateRequiredString(basket?.launch_date)) {
    errors.launch_date = 'Select a valid date';
  }

  if (
    basket?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY &&
    !validateRequiredString(basket?.launch_point)
  ) {
    errors.launch_point = 'Launch point is required';
  }

  return errors;
};

/**
 * Validates symbol and ticker length
 * @param {Object} basket - The basket object to validate
 * @param {string} authOrgPrefix - The authenticated user's org prefix
 * @param {boolean} isSubmitBasket - Whether this is a submit operation
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateSymbol = (basket, authOrgPrefix, isSubmitBasket) => {
  const errors = {};

  if (isSubmitBasket) {
    if (!validateRequiredString(basket?.symbol)) {
      errors.symbol = 'Symbol is required';
    } else {
      const tickerLength = calculateTickerLength(basket.symbol, basket.org_prefix, authOrgPrefix);
      const orgPrefixLength = basket.org_prefix?.length ?? authOrgPrefix?.length ?? 0;

      if (tickerLength > MAX_TICKER_LENGTH) {
        errors.symbol = `Character limit exceeded: Maximum of ${MAX_TICKER_LENGTH -
          orgPrefixLength} characters allowed.`;
      }
    }
  }

  return errors;
};

/**
 * Validates effective date for submit mode
 * @param {Object} basket - The basket object to validate
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateEffectiveDate = basket => {
  const errors = {};

  if (!validateRequiredString(basket?.effective_date)) {
    errors.effective_date = 'Effective date is required';
  }

  return errors;
};

/**
 * Validates special case: must not send launch date
 * @param {Object} basket - The basket object to validate
 * @param {Object} enableDivisorOrStartingValue - Object mapping symbols to divisor/starting value status
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateSpecialCase = (basket, enableDivisorOrStartingValue) => {
  const errors = {};
  const symbol = basket?.symbol;
  const divisorOrStartingValue = enableDivisorOrStartingValue?.[symbol];

  if (
    divisorOrStartingValue?.statusStarting &&
    basket?.divisor_determination_setup === DIVISOR_DETERMINATION_VALUES.IMMEDIATELY &&
    basket?.calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY &&
    basket?.launch_point === DATA_CALCULATION_SETUP_VALUES.EOD
  ) {
    errors.must_not_to_send_launch_date = 'This case must not be sent';
  }

  return errors;
};

/**
 * Validates cash distribution settings for submit mode
 * @param {Object} basket - The basket object to validate
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateCashDistribution = basket => {
  const errors = {};
  if (basket?.calculation_type === PRICE_RETURN) {
    return errors;
  }
  if (!basket?.additional_settings?.m_cash_distribution_type) {
    errors.cash_distribution = 'Cash Distribution is required';
  }

  if (!basket?.additional_settings?.cash_distribution_application_point) {
    errors.cash_distribution_application_point = 'Please select an application point';
  }

  return errors;
};

/**
 * Validates interest rate on cash
 * @param {Object} basket - The basket object to validate
 * @param {Function} existsCashConstituentsInMetadata - Function to check if cash constituents exist
 * @returns {Object} Object with field names as keys and error messages as values
 */
const validateInterestRateOnCash = (basket, existsCashConstituentsInMetadata) => {
  const errors = {};

  if (
    basket?.additional_settings?.cash_constituent ||
    existsCashConstituentsInMetadata(basket?.symbol)
  ) {
    const interestRate = basket?.additional_settings?.cash_constituent?.interest_rate_on_cash;

    if (!validateInterestRate(interestRate)) {
      errors.interest_rate_on_cash = 'Valid interest rate is required';
    }
  }

  return errors;
};

/**
 * Returns a map of basket symbol -> list of instrument identifiers (sedol or isin)
 * for instruments that have no shares (missing, 0, null, or undefined).
 *
 * @param {Array} selectedBasketsData - List of basket objects with meta_reference_data
 * @param {boolean} isUBSUser - If true, use sedol; otherwise use isin
 * @returns {Object} Map of basket.symbol to array of identifiers
 */
export const getBasketsWithoutShares = (selectedBasketsData, isUBSUser) => {
  const result = {};

  if (!selectedBasketsData) {
    return result;
  }

  selectedBasketsData.forEach(basket => {
    const instrumentsWithoutShares = basket?.meta_reference_data?.filter(
      instrument =>
        !instrument?.shares ||
        instrument.shares === 0 ||
        instrument.shares === null ||
        instrument.shares === undefined,
    );

    if (instrumentsWithoutShares && instrumentsWithoutShares.length > 0) {
      result[basket.symbol] = instrumentsWithoutShares
        .map(instrument => (isUBSUser ? instrument.sedol : instrument.isin))
        .filter(Boolean);
    }
  });

  return result;
};

/**
 * Returns a map of basket symbol -> list of instrument identifiers (sedol or isin)
 * for instruments that do not have coverage (type !== WITH_COVERAGE).
 *
 * @param {Array} selectedBasketsData - List of basket objects with meta_reference_data
 * @param {boolean} isUBSUser - If true, use sedol; otherwise use isin
 * @returns {Object} Map of basket.symbol to array of identifiers
 */
export const getBasketsWithoutCoverageInstruments = (selectedBasketsData, isUBSUser) => {
  const result = {};

  (selectedBasketsData || []).forEach(basket => {
    const instruments = basket?.meta_reference_data || [];

    const instrumentsWithoutCoverage = instruments
      .filter(instrument => instrument?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE)
      .map(instrument => (isUBSUser ? instrument.sedol : instrument.isin))
      .filter(Boolean);

    if (instrumentsWithoutCoverage.length > 0) {
      result[basket.symbol] = instrumentsWithoutCoverage;
    }
  });

  return result;
};

// ============================================================================
// MAIN VALIDATION FUNCTION
// ============================================================================

/**
 * Main validation function for baskets
 * @param {Object} params - Validation parameters
 * @param {Array} params.basketList - Array of baskets to validate
 * @param {Object} params.basketsWithoutShares - Object mapping symbols to arrays of identifiers without shares
 * @param {Object} params.basketsWithoutCoverageInstruments - Object mapping symbols to arrays of identifiers without coverage
 * @param {string} params.authOrgPrefix - The authenticated user's org prefix
 * @param {boolean} params.isSubmitBasket - Whether this is a submit operation
 * @param {Object} params.enableDivisorOrStartingValue - Object mapping symbols to divisor/starting value status
 * @param {boolean} params.shouldSendLaunchDate - Whether launch date should be validated
 * @param {Function} params.existsCashConstituentsInMetadata - Function to check if cash constituents exist
 * @returns {Object} Object with structure { [symbol]: { [field]: message } }
 */
export const validateBasketsForm = ({
  basketList = [],
  basketsWithoutShares = {},
  basketsWithoutCoverageInstruments = {},
  authOrgPrefix = '',
  isSubmitBasket = false,
  enableDivisorOrStartingValue = {},
  shouldSendLaunchDate = false,
  existsCashConstituentsInMetadata,
  isUserUploadingData = false,
}) => {
  // Input validation
  if (!Array.isArray(basketList)) {
    console.warn('validateBasketsForm: basketList must be an array');
    return {};
  }

  if (typeof existsCashConstituentsInMetadata !== 'function') {
    console.warn('validateBasketsForm: existsCashConstituentsInMetadata must be a function');
    return {};
  }

  const isUBSUser = authOrgPrefix === ORGANIZATION_PREFIXES.UBS;
  const effectiveBasketsWithoutShares = isUserUploadingData
    ? getBasketsWithoutShares(basketList, isUBSUser)
    : basketsWithoutShares;
  const effectiveBasketsWithoutCoverageInstruments = isUserUploadingData
    ? getBasketsWithoutCoverageInstruments(basketList, isUBSUser)
    : basketsWithoutCoverageInstruments;

  const allBasketsErrors = {};

  basketList.forEach(basket => {
    const errors = {};

    // Basic required fields (always validated)
    Object.assign(errors, validateBasicRequiredFields(basket));

    // Shares validation
    Object.assign(errors, validateShares(basket, effectiveBasketsWithoutShares, authOrgPrefix));

    // Coverage validation
    Object.assign(
      errors,
      validateCoverage(basket, effectiveBasketsWithoutCoverageInstruments, authOrgPrefix),
    );

    // Divisor validation (different for update vs submit)
    if (!isSubmitBasket) {
      Object.assign(errors, validateDivisorForUpdate(basket));
      Object.assign(errors, validateCashDistribution(basket));
    } else {
      Object.assign(errors, validateDivisorForSubmit(basket, enableDivisorOrStartingValue));
      Object.assign(errors, validateCashDistribution(basket));
    }

    // Submit-specific validations
    if (isSubmitBasket) {
      Object.assign(errors, validateLaunchFields(basket, shouldSendLaunchDate));
      Object.assign(errors, validateSymbol(basket, authOrgPrefix, isSubmitBasket));
      Object.assign(errors, validateEffectiveDate(basket));
      Object.assign(errors, validateSpecialCase(basket, enableDivisorOrStartingValue));
      Object.assign(errors, validateCashDistribution(basket));
    }

    // Interest rate validation (always validated if cash constituents exist)
    Object.assign(errors, validateInterestRateOnCash(basket, existsCashConstituentsInMetadata));

    // Only add errors object if it has errors
    if (Object.keys(errors).length > 0) {
      allBasketsErrors[basket?.symbol] = errors;
    }
  });
  if (isUserUploadingData && Object.keys(allBasketsErrors).length > 0) {
    generateBulkBasketErrorToast();
  }

  return allBasketsErrors;
};
